import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Switch } from './ui/switch';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from './ui/dialog';
import { Loader2, X, Plus, Upload, ImageIcon } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const BOOKABLE_TYPES = [
  { value: 'meeting_room', label: 'Meeting Room' },
  { value: 'conference_room', label: 'Conference Room' },
  { value: 'day_pass', label: 'Day Pass' },
  { value: 'dedicated_desk', label: 'Dedicated Desk' },
  { value: 'event_space', label: 'Event Space' },
];

// Mapping from bookable type to booking mode and pricing labels
const TYPE_CONFIG = {
  meeting_room:    { mode: 'hourly',  rateLabel: 'Rate per Hour (₹)',   unit: '/hr',    capacityLabel: 'Seating Capacity' },
  conference_room: { mode: 'hourly',  rateLabel: 'Rate per Hour (₹)',   unit: '/hr',    capacityLabel: 'Seating Capacity' },
  day_pass:        { mode: 'daily',   rateLabel: 'Rate per Day (₹)',    unit: '/day',   capacityLabel: 'Max Spots per Day' },
  dedicated_desk:  { mode: 'monthly', rateLabel: 'Rate per Month (₹)',  unit: '/month', capacityLabel: 'Seats Available' },
  event_space:     { mode: 'hourly',  rateLabel: 'Rate per Hour (₹)',   unit: '/hr',    capacityLabel: 'Seating Capacity' },
};

const AMENITY_SUGGESTIONS = [
  'Wifi', 'Whiteboard', 'TV Screen', 'AC', 'Video Conferencing',
  'Projector', 'Sound System', 'Power Outlets', 'Printer', 'Scanner',
];

const INCLUDED_SUGGESTIONS = [
  'Tea/Coffee', 'High-speed Wifi', 'Printing (10 pages)', 'Water',
  'Locker', 'Stationery', 'Parking (1 slot)',
];

const NOT_INCLUDED_SUGGESTIONS = [
  'Catering', 'Parking', 'External AV equipment', 'Photography',
  'Extra furniture', 'Storage beyond locker',
];

const defaultForm = {
  name: '',
  venue_id: '',
  bookable_type: 'meeting_room',
  capacity: 4,
  hourly_rate: 600,
  half_day_rate: '',
  full_day_rate: '',
  description: '',
  amenities: [],
  whats_included: [],
  whats_not_included: [],
  images: [],
  min_booking_hours: 1,
  max_booking_hours: 8,
  buffer_minutes: 15,
  cancellation_hours: 24,
  operating_hours_start: '09:00',
  operating_hours_end: '21:00',
  gst_applicable: true,
  gst_rate: 18.0,
  is_active: true,
  booking_mode: 'hourly',
  offer_enabled: false,
  offer_title: '',
  offer_description: '',
  offer_badge: '',
  offer_valid_until: '',
};

export const BookableFormModal = ({ open, onClose, api, onSuccess, venues, editBookable, preselectedVenueId }) => {
  const [form, setForm] = useState(defaultForm);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [newAmenity, setNewAmenity] = useState('');
  const [newIncluded, setNewIncluded] = useState('');
  const [newNotIncluded, setNewNotIncluded] = useState('');
  const isEdit = !!editBookable;

  useEffect(() => {
    if (editBookable) {
      setForm({
        ...defaultForm,
        ...editBookable,
        half_day_rate: editBookable.half_day_rate || '',
        full_day_rate: editBookable.full_day_rate || '',
        amenities: editBookable.amenities || [],
        whats_included: editBookable.whats_included || [],
        whats_not_included: editBookable.whats_not_included || [],
        images: editBookable.images || [],
        operating_hours_start: editBookable.operating_hours_start || '09:00',
        operating_hours_end: editBookable.operating_hours_end || '21:00',
        booking_mode: editBookable.booking_mode || TYPE_CONFIG[editBookable.bookable_type]?.mode || 'hourly',
        offer_enabled: editBookable.offer_enabled || false,
        offer_title: editBookable.offer_title || '',
        offer_description: editBookable.offer_description || '',
        offer_badge: editBookable.offer_badge || '',
        offer_valid_until: editBookable.offer_valid_until || '',
      });
    } else {
      setForm({ ...defaultForm, venue_id: preselectedVenueId || '' });
    }
  }, [editBookable, preselectedVenueId, open]);

  const update = (field, value) => setForm(f => ({ ...f, [field]: value }));

  // Auto-set booking_mode when type changes
  const handleTypeChange = (type) => {
    const config = TYPE_CONFIG[type] || TYPE_CONFIG.meeting_room;
    setForm(f => ({ ...f, bookable_type: type, booking_mode: config.mode }));
  };

  const addTag = (field, value, setter) => {
    if (value.trim() && !form[field].includes(value.trim())) {
      update(field, [...form[field], value.trim()]);
    }
    setter('');
  };

  const removeTag = (field, value) => {
    update(field, form[field].filter(v => v !== value));
  };

  // Image upload handler
  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    setUploading(true);
    try {
      const formData = new FormData();
      files.forEach(file => formData.append('files', file));

      const res = await api.post('/upload/images', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const newUrls = res.data.urls || [];
      update('images', [...form.images, ...newUrls]);
      toast.success(`${newUrls.length} image(s) uploaded`);
    } catch (err) {
      toast.error('Failed to upload images');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const removeImage = (idx) => {
    update('images', form.images.filter((_, i) => i !== idx));
  };

  const handleSubmit = async () => {
    if (!form.name || !form.venue_id || !form.bookable_type) {
      toast.error('Name, venue, and type are required');
      return;
    }
    if (form.capacity < 1) {
      toast.error('Capacity must be at least 1');
      return;
    }
    if (form.hourly_rate <= 0) {
      toast.error('Rate must be positive');
      return;
    }

    setSaving(true);
    try {
      const config = TYPE_CONFIG[form.bookable_type] || TYPE_CONFIG.meeting_room;
      const payload = {
        ...form,
        capacity: parseInt(form.capacity),
        hourly_rate: parseFloat(form.hourly_rate),
        half_day_rate: form.half_day_rate ? parseFloat(form.half_day_rate) : null,
        full_day_rate: form.full_day_rate ? parseFloat(form.full_day_rate) : null,
        min_booking_hours: parseInt(form.min_booking_hours),
        max_booking_hours: parseInt(form.max_booking_hours),
        buffer_minutes: parseInt(form.buffer_minutes),
        cancellation_hours: parseInt(form.cancellation_hours),
        gst_rate: parseFloat(form.gst_rate),
        booking_mode: config.mode,
      };

      if (isEdit) {
        const { id, created_at, venue_id, ...updatePayload } = payload;
        await api.patch(`/bookables/${editBookable.id}`, updatePayload);
        toast.success('Bookable updated');
      } else {
        await api.post('/bookables', payload);
        toast.success('Bookable created');
      }
      onSuccess?.();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to ${isEdit ? 'update' : 'create'} bookable`);
    } finally {
      setSaving(false);
    }
  };

  const typeConfig = TYPE_CONFIG[form.bookable_type] || TYPE_CONFIG.meeting_room;
  const isHourly = typeConfig.mode === 'hourly';

  const TagInput = ({ label, field, suggestions, value, setValue, testIdPrefix }) => (
    <div className="space-y-2">
      <Label className="text-sm font-medium">{label}</Label>
      <div className="flex flex-wrap gap-1.5 mb-2">
        {form[field].map(tag => (
          <Badge key={tag} variant="secondary" className="gap-1 text-xs">
            {tag}
            <X className="w-3 h-3 cursor-pointer" onClick={() => removeTag(field, tag)} />
          </Badge>
        ))}
      </div>
      <div className="flex gap-2">
        <Input
          placeholder={`Add ${label.toLowerCase()}...`}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addTag(field, value, setValue))}
          className="h-9 text-sm"
          data-testid={`${testIdPrefix}-input`}
        />
        <Button type="button" variant="outline" size="sm" onClick={() => addTag(field, value, setValue)}>
          <Plus className="w-3 h-3" />
        </Button>
      </div>
      {suggestions.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-1">
          {suggestions.filter(s => !form[field].includes(s)).slice(0, 5).map(s => (
            <button key={s} type="button" className="text-[10px] px-2 py-0.5 rounded-full bg-muted hover:bg-muted/80 text-muted-foreground transition-colors"
              onClick={() => addTag(field, s, setValue)}>+ {s}</button>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="bookable-form-modal">
        <DialogHeader>
          <DialogTitle className="font-heading">{isEdit ? 'Edit Bookable' : 'Create Bookable'}</DialogTitle>
        </DialogHeader>

        <div className="space-y-5 py-2">
          {/* Row 1: Name + Type */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Name *</Label>
              <Input value={form.name} onChange={(e) => update('name', e.target.value)} placeholder="e.g., 4 Seater Meeting Room" data-testid="bookable-name-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Bookable Type *</Label>
              <Select value={form.bookable_type} onValueChange={handleTypeChange}>
                <SelectTrigger data-testid="bookable-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {BOOKABLE_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Row 2: Venue + Capacity */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Venue *</Label>
              <Select value={form.venue_id} onValueChange={(v) => update('venue_id', v)} disabled={isEdit}>
                <SelectTrigger data-testid="bookable-venue-select"><SelectValue placeholder="Select venue" /></SelectTrigger>
                <SelectContent>
                  {venues?.map(v => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>{typeConfig.capacityLabel} *</Label>
              <Input type="number" min={1} value={form.capacity} onChange={(e) => update('capacity', e.target.value)} data-testid="bookable-capacity-input" />
            </div>
          </div>

          {/* Row 3: Pricing - context-aware */}
          <div className={`grid ${isHourly ? 'grid-cols-3' : 'grid-cols-1'} gap-4`}>
            <div className="space-y-1.5">
              <Label>{typeConfig.rateLabel} *</Label>
              <Input type="number" min={0} value={form.hourly_rate} onChange={(e) => update('hourly_rate', e.target.value)} data-testid="bookable-hourly-rate" />
              <p className="text-[10px] text-muted-foreground">Price per {typeConfig.mode === 'monthly' ? 'person per month' : typeConfig.mode === 'daily' ? 'user per day' : 'hour'} + GST</p>
            </div>
            {isHourly && (
              <>
                <div className="space-y-1.5">
                  <Label>Half-Day Rate (₹)</Label>
                  <Input type="number" min={0} value={form.half_day_rate} onChange={(e) => update('half_day_rate', e.target.value)} placeholder="Auto if blank" />
                </div>
                <div className="space-y-1.5">
                  <Label>Full-Day Rate (₹)</Label>
                  <Input type="number" min={0} value={form.full_day_rate} onChange={(e) => update('full_day_rate', e.target.value)} placeholder="Auto if blank" />
                </div>
              </>
            )}
          </div>

          {/* Image Upload Section */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Photos *</Label>
            <p className="text-[10px] text-muted-foreground -mt-1">Upload photos of this space so clients can see how it looks</p>
            
            {/* Image Preview Grid */}
            {form.images.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {form.images.map((url, idx) => {
                  const fullUrl = url.startsWith('/') ? `${BACKEND_URL}${url}` : url;
                  return (
                    <div key={idx} className="relative group w-24 h-24 rounded-lg overflow-hidden border border-border">
                      <img src={fullUrl} alt={`Space ${idx + 1}`} className="w-full h-full object-cover" />
                      <button
                        type="button"
                        onClick={() => removeImage(idx)}
                        className="absolute top-1 right-1 w-5 h-5 rounded-full bg-red-500 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Upload Button */}
            <label className="flex items-center gap-2 px-4 py-3 rounded-lg border-2 border-dashed border-border hover:border-primary/50 cursor-pointer transition-colors bg-muted/30" data-testid="bookable-image-upload">
              <input
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={handleImageUpload}
                disabled={uploading}
              />
              {uploading ? (
                <Loader2 className="w-5 h-5 animate-spin text-primary" />
              ) : (
                <Upload className="w-5 h-5 text-muted-foreground" />
              )}
              <span className="text-sm text-muted-foreground">
                {uploading ? 'Uploading...' : form.images.length > 0 ? 'Add more photos' : 'Click to upload photos'}
              </span>
              {form.images.length > 0 && (
                <Badge variant="secondary" className="ml-auto text-xs">{form.images.length} photo(s)</Badge>
              )}
            </label>
          </div>

          {/* Description */}
          <div className="space-y-1.5">
            <Label>Description</Label>
            <Textarea value={form.description} onChange={(e) => update('description', e.target.value)} rows={2} placeholder="Brief description of the space..." data-testid="bookable-description" />
          </div>

          {/* Amenities */}
          <TagInput label="Amenities" field="amenities" suggestions={AMENITY_SUGGESTIONS} value={newAmenity} setValue={setNewAmenity} testIdPrefix="amenity" />

          {/* What's Included */}
          <TagInput label="What's Included" field="whats_included" suggestions={INCLUDED_SUGGESTIONS} value={newIncluded} setValue={setNewIncluded} testIdPrefix="included" />

          {/* What's Not Included */}
          <TagInput label="What's Not Included" field="whats_not_included" suggestions={NOT_INCLUDED_SUGGESTIONS} value={newNotIncluded} setValue={setNewNotIncluded} testIdPrefix="not-included" />

          {/* Operating Hours - only for hourly types */}
          {isHourly && (
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Operating Hours Start</Label>
                <Input type="time" value={form.operating_hours_start} onChange={(e) => update('operating_hours_start', e.target.value)} data-testid="bookable-hours-start" />
              </div>
              <div className="space-y-1.5">
                <Label>Operating Hours End</Label>
                <Input type="time" value={form.operating_hours_end} onChange={(e) => update('operating_hours_end', e.target.value)} data-testid="bookable-hours-end" />
              </div>
            </div>
          )}

          {/* Booking Rules - only for hourly types */}
          {isHourly && (
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-1.5">
                <Label>Min Booking (hrs)</Label>
                <Input type="number" min={1} value={form.min_booking_hours} onChange={(e) => update('min_booking_hours', e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label>Max Booking (hrs)</Label>
                <Input type="number" min={1} value={form.max_booking_hours} onChange={(e) => update('max_booking_hours', e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label>Buffer (mins)</Label>
                <Input type="number" min={0} value={form.buffer_minutes} onChange={(e) => update('buffer_minutes', e.target.value)} />
              </div>
            </div>
          )}

          {/* Promotional Offer Section */}
          <div className={`p-4 rounded-lg border ${form.offer_enabled ? 'border-primary/40 bg-primary/5' : 'border-border'}`}>
            <div className="flex items-center justify-between mb-3">
              <div>
                <Label className="text-sm font-semibold">Promotional Offer Banner</Label>
                <p className="text-[10px] text-muted-foreground mt-0.5">Show a promo banner on this space's booking card</p>
              </div>
              <Switch
                checked={form.offer_enabled}
                onCheckedChange={(v) => update('offer_enabled', v)}
                data-testid="bookable-offer-enabled-toggle"
              />
            </div>
            {form.offer_enabled && (
              <div className="grid gap-3">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label>Offer Title</Label>
                    <Input
                      placeholder="e.g., Early Bird Special"
                      value={form.offer_title}
                      onChange={(e) => update('offer_title', e.target.value)}
                      data-testid="bookable-offer-title"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Badge Text</Label>
                    <Input
                      placeholder="e.g., 15% OFF"
                      value={form.offer_badge}
                      onChange={(e) => update('offer_badge', e.target.value)}
                      data-testid="bookable-offer-badge"
                    />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label>Offer Description</Label>
                  <Textarea
                    placeholder="Brief description of the offer shown on the card..."
                    value={form.offer_description}
                    onChange={(e) => update('offer_description', e.target.value)}
                    rows={2}
                    data-testid="bookable-offer-description"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>Valid Until (optional)</Label>
                  <Input
                    type="date"
                    value={form.offer_valid_until}
                    onChange={(e) => update('offer_valid_until', e.target.value)}
                    className="max-w-[200px]"
                    data-testid="bookable-offer-valid-until"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Toggles */}
          <div className="flex items-center justify-between gap-4 pt-2 border-t border-border">
            <div className="flex items-center gap-3">
              <Switch checked={form.is_active} onCheckedChange={(v) => update('is_active', v)} data-testid="bookable-active-toggle" />
              <Label>{form.is_active ? 'Enabled' : 'Disabled'}</Label>
            </div>
            <div className="flex items-center gap-3">
              <Switch checked={form.gst_applicable} onCheckedChange={(v) => update('gst_applicable', v)} />
              <Label>GST ({form.gst_rate}%)</Label>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={saving} data-testid="bookable-form-submit">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            {isEdit ? 'Save Changes' : 'Create Bookable'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
